import { observer } from 'src/utils/mobx-react';
import React, { ChangeEvent, useState } from 'react';
import { AppState, useAppStateContext } from 'src/AppState/AppState';
// eslint-disable-next-line no-restricted-imports
import { useAsObservableSource } from 'mobx-react-lite';
import { computed, makeAutoObservable } from 'mobx';
import { MarketId } from 'src_common/common/websocket2/id/WebsocketId';

class SelectState {
    public constructor(
        private readonly props: SelectPropsType,
        public readonly appState: AppState
    ) {}

    public onChangeHandler = async (event: ChangeEvent<HTMLSelectElement>): Promise<void> => {
        await this.props.state.onChangeTagHandler(event, this.props.tag.key);
    };
}

interface SelectPropsType {
    tag: InnerTagType;
    state: MarketTagsState;
}

const Select = observer('Select', (propsIn: SelectPropsType) => {
    const appState = useAppStateContext();
    const props = useAsObservableSource(propsIn);
    const [state] = useState(() => new SelectState(props, appState));
    const _tagName = `tags.${props.tag.key}.0`;
    return (
        <div className='element'>
            <div className='select-wrapper'>
                <select
                    name={_tagName}
                    onChange={state.onChangeHandler}
                >
                    <option />
                    {props.tag.values.map((value) => (
                        <option
                            key={value.id}
                            value={value.id}
                            selected={value.id === props.tag.currentSelected}
                        >
                            {value.label}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
});

interface InnerTagType {
    key: string;
    label: string;
    currentSelected: string;
    values: Array<{
        id: string;
        isDefault: boolean;
        label: string;
    }>;
}

class MarketTagsState {
    public constructor(
        private readonly appState: AppState,
        private readonly props: MarketTagsPropsType
    ) {
        makeAutoObservable(this);
    }

    @computed public get marketTags(): Array<InnerTagType> {
        const marketId = this.props.marketId;
        const marketTags: Array<InnerTagType> = [];
        const marketModel = marketId.getModel();
        const eventId = marketModel?.eventId;
        if (eventId === undefined) {
            return [];
        }
        const tags = this.appState.sdkStaff.getTags(eventId, 'market');
        for (const tag of tags) {
            const currentTagValue = marketModel?.getData().tags[tag.name];
            const defTagValue = tag.values.find((tag) => tag.isDefault)?.id ?? '-';
            marketTags.push({
                key: tag.name,
                label: tag.label,
                currentSelected: currentTagValue ?? defTagValue,
                values: tag.values,
            });
        }

        return marketTags;
    }

    public onChangeTagHandler = async (event: ChangeEvent<HTMLSelectElement>, tagId: string): Promise<void> => {
        const marketId = this.props.marketId;
        const marketModel = marketId.getModel();
        if (marketModel === null) {
            return;
        }
        const rawData = {
            ...marketModel.getData().tags,
            [tagId]: event.target.value,
        };

        let data = {};
        for (const [id, value] of Object.entries(rawData)) {
            data = {
                ...data,
                [id]: [value],
            };
        }

        if (event.target.value === 'by-display-order') {
            this.props.onSetSelectionOrder(marketId);
        }
        await this.appState.eventStateActions.saveMarketChanges(marketModel.id2, { tags: data });
    };
}

type MarketTagsPropsType = {
    marketId: MarketId;
    onSetSelectionOrder: (marketId: MarketId) => void;
};

export const MarketTags = observer('MarketTags', (propsIn: MarketTagsPropsType) => {
    const props = useAsObservableSource(propsIn);
    const appState = useAppStateContext();
    const [state] = useState(() => new MarketTagsState(appState, props));

    return (
        <div className='col-2 form'>
            <h4>Tags</h4>
            <table className='w-full'>
                <thead>
                    <tr>
                        <th className='w-1/3'>Tag name</th>
                        <th className='w-2/3'>Value</th>
                    </tr>
                </thead>
                <tbody>
                    {state.marketTags.map((tag) => (
                        <tr key={tag.key}>
                            <td className='w-1/3 text-wrap'>{tag.label}</td>
                            <td className='w-2/3'>
                                <Select
                                    tag={tag}
                                    state={state}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
});
